/**
 * The Merchant Locations list: markers a partner's branded rider app shows on
 * its map. Organization-scoped, server-paginated.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Card from '@mui/material/Card';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MenuItem from '@mui/material/MenuItem';
import { SelectChangeEvent } from '@mui/material/Select';

import { PaginationTable } from '../../components/Redesing/table/pagination-table';
import { MovaticFieldGroupSingleSelectFilter } from '../../components/Redesing/movatic-field-group';
import { merchantStoreColumns } from '../../tableColumns/merchantStoreColumns';
import {
  useMerchantStoresRelayQuery,
  mapMerchantStores,
} from '../../hooks/merchantStores/query/V2';
import type {
  MerchantStoreQueryFilter,
  MerchantStoreState,
} from '../../hooks/merchantStores/query/V2';
import useOrganization from '../../hooks/use-organization';
import useUrlState from '../../hooks/use-url-state';
import { useKeysetPagination } from '../../hooks/use-keyset-pagination';
import useSearchDebounce from '../../hooks/search/useSearchDebounce';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants';
import { history } from '../../utils';
import { SearchChip } from '../../ts/interface/components/table.interface';
import type { ISortConfig } from '../../ts/interface/components/redesing/table.interface';

const STORE_STATUS: { value: string; label: string }[] = [
  { value: 'LIVE', label: 'Live' },
  { value: 'RETIRED', label: 'Retired' },
  { value: 'all', label: 'All' },
];

const sortKeyToField = (key: string) =>
  ({ name: 'NAME', website_url: 'WEBSITE_URL' }[key] ?? 'NAME');

const MerchantStoreIndex = () => {
  const organizationInfo = useOrganization();
  const organizationId = organizationInfo?.id ?? '';

  const [selectFilter, setSelectFilter] = useState<string>(STORE_STATUS[0].value);
  const [exportData, setExportData] = useState(false);

  const { pageNumber, after, isPaginating, onPageChange, onPageLoaded, reset } =
    useKeysetPagination();

  const [pageState, setPageState, isComplete, isRestoring] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    status: STORE_STATUS[0].value,
    search: null as string | null,
    sortConfig: { key: 'name', direction: 'asc' } as ISortConfig,
  });

  const [chips, setChips] = useState<SearchChip[]>([]);
  const debouncedSearch = useSearchDebounce(pageState.search);

  const graphqlFilter = useMemo((): MerchantStoreQueryFilter => {
    const filter: MerchantStoreQueryFilter = { organizationId };
    if (pageState.status && pageState.status !== 'all') {
      filter.state = pageState.status as MerchantStoreState;
    }
    if (debouncedSearch) filter.name = debouncedSearch;
    return filter;
  }, [organizationId, pageState.status, debouncedSearch]);

  const graphqlSort = useMemo(
    () => [
      {
        field: sortKeyToField(pageState.sortConfig.key) as any,
        order: pageState.sortConfig.direction.toUpperCase() as 'ASC' | 'DESC',
      },
    ],
    [pageState.sortConfig.key, pageState.sortConfig.direction]
  );

  const { data, count, hasMore, endCursor, isLoading, isRefetching } =
    useMerchantStoresRelayQuery(graphqlFilter, {
      limit: pageState.sizePerPage,
      after,
      sort: graphqlSort,
      enabled: Boolean(organizationId) && isComplete && !isRestoring,
    });

  const stores = useMemo(() => (data ? mapMerchantStores(data) : []), [data]);

  // Record the next page's cursor and clear the paginating loader.
  useEffect(() => {
    onPageLoaded(endCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (isRestoring) return;
    const next: SearchChip[] = [];
    if (pageState.status && pageState.status !== 'all') {
      const option = STORE_STATUS.find((item) => item.value === pageState.status);
      next.push({
        label: 'Status',
        field: 'status',
        value: pageState.status,
        displayValue: option?.label ?? pageState.status,
      });
    }
    setChips(next);
  }, [isRestoring, pageState.status]);

  const handleChipDelete = useCallback(
    (deleted: SearchChip) => {
      if (deleted.label !== 'Status') return;
      reset();
      setSelectFilter('all');
      setPageState((prev: any) => ({
        ...prev,
        sizePerPage: DEFAULT_ROWS_PER_PAGE,
        status: 'all',
      }));
    },
    [setPageState, reset]
  );

  return (
    <Card>
      <PaginationTable
        dataId="merchant-locations-list"
        isLoading={!isComplete || isRestoring || isLoading || isPaginating}
        isLoadingCache={isRefetching}
        columns={merchantStoreColumns as any[]}
        items={stores}
        customKey="id"
        noDataText="No merchant locations yet"
        localSearch={false}
        noDataSlice
        showFilter
        buttons={[
          {
            label: 'Export',
            startIcon: <FileDownloadIcon />,
            onClick: () => setExportData(true),
          },
          {
            label: 'Add',
            startIcon: <AddIcon />,
            onClick: () => history.push('/branded-app/merchant-locations/add'),
          },
        ]}
        exportData={exportData}
        csvFileName="MerchantLocations.csv"
        onExport={() => setExportData(false)}
        chips={chips}
        handleChipDelete={handleChipDelete}
        spaceFilters={
          <MovaticFieldGroupSingleSelectFilter
            dataId="merchant-location-status-filter"
            label="Status"
            value={selectFilter}
            onChange={(e: SelectChangeEvent) => setSelectFilter(e.target.value as string)}
            options={STORE_STATUS.map((option, index) => (
              <MenuItem
                id={`merchant-location-status-${index}`}
                key={option.value}
                value={option.value}
              >
                {option.label}
              </MenuItem>
            ))}
          />
        }
        onApplyFilter={() => {
          if (selectFilter !== pageState.status) {
            reset();
            setPageState((prev: any) => ({
              ...prev,
              sizePerPage: DEFAULT_ROWS_PER_PAGE,
              status: selectFilter,
            }));
          }
        }}
        onRowCLick={(id: string) =>
          history.push(`/branded-app/merchant-locations/${id}/general`)
        }
        sortBy={pageState.sortConfig.key}
        sortDirection={pageState.sortConfig.direction}
        onSortChange={(sort: { key: string; direction: string }) => {
          reset();
          setPageState((prev: any) => ({
            ...prev,
            sortConfig: { key: sort.key, direction: sort.direction as 'asc' | 'desc' },
          }));
        }}
        padding={{ pt: 1, pr: 1 }}
        page={pageNumber}
        rowsPerPage={pageState.sizePerPage}
        hasMore={hasMore}
        totalItems={count}
        onPageChange={(_event, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          reset();
          setPageState((prev: any) => ({
            ...prev,
            sizePerPage: Number(event.target.value),
          }));
        }}
        handleSearch={(e: React.ChangeEvent<HTMLInputElement>) => {
          reset();
          setPageState((prev: any) => ({ ...prev, search: e.target.value }));
        }}
        searchText={pageState.search || ''}
        searchPlaceholder="Search merchant locations"
      />
    </Card>
  );
};

export default MerchantStoreIndex;
